// File: src/components/potus/PresidentialSchedule.jsx
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatDate } from '../../utils/constants';
import TitleAndInfo from '../TitleAndInfo';
import EventIcon from '@mui/icons-material/Event';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const parseTime = (timeStr) => {
  // Converts "3:15 AM" → Date object (today's date)
  const [hourStr, minuteStr] = timeStr.split(/[: ]/);
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  const isPM = timeStr.includes('PM');
  const actualHour = isPM && hour !== 12 ? hour + 12 : !isPM && hour === 12 ? 0 : hour;

  const date = new Date();
  date.setHours(actualHour);
  date.setMinutes(minute);
  date.setSeconds(0);
  date.setMilliseconds(0);
  return date;
};

const PresidentialSchedule = ({ data, loading }) => {
  const theme = useTheme();

  // Group events by formatted date string
  const groupedByDate = data.reduce((acc, event) => {
    const dateKey = formatDate(event.date);
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(event);
    return acc;
  }, {});

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <Typography>Loading schedule...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3, pl: 2 }}>
        <TitleAndInfo
          titleTop="Presidential Schedule:"
          title=""
          info="Live presidential events, meetings, and official activities from the White House."
        />
      </Box>

      <Paper
        variant="stats"
        sx={{
          p: 3,
          backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(19, 47, 76, 0.4)'
            : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        {Object.entries(groupedByDate).length === 0 ? (
          <Typography variant="body1" align="center" p={2}>
            No scheduled events available.
          </Typography>
        ) : (
          Object.entries(groupedByDate).map(([date, events], dateIndex) => {
            const sortedEvents = [...events].sort((a, b) => {
              const timeA = parseTime(a.metadata?.time_formatted || '12:00 AM');
              const timeB = parseTime(b.metadata?.time_formatted || '12:00 AM');
              return timeB - timeA; // Newest first
            });

            return (
              <Box key={date} mb={4}>
                {/* Date Header */}
                <Box
                  display="flex"
                  alignItems="center"
                  mb={2}
                  pb={1}
                  borderBottom={1}
                  borderColor="divider"
                >
                  <EventIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6" component="h3" fontWeight="bold">
                    {date}
                  </Typography>
                  {dateIndex === 0 && (
                    <Chip
                      label="Today"
                      color="primary"
                      size="small"
                      sx={{ ml: 2 }}
                    />
                  )}
                </Box>

                {/* Events List */}
                <List>
                  {sortedEvents.map((event, eventIndex) => (
                    <React.Fragment key={event.unique_id || eventIndex}>
                      <ListItem alignItems="flex-start">
                        <ListItemIcon>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              position: 'relative'
                            }}
                          >
                            <FiberManualRecordIcon
                              sx={{
                                color: dateIndex === 0 && eventIndex === 0
                                  ? theme.palette.primary.main
                                  : theme.palette.grey[400],
                                fontSize: 12
                              }}
                            />
                            {eventIndex < sortedEvents.length - 1 && (
                              <Box
                                sx={{
                                  width: 2,
                                  height: 40,
                                  backgroundColor: theme.palette.grey[300],
                                  mt: 1
                                }}
                              />
                            )}
                          </Box>
                        </ListItemIcon>

                        <ListItemText
                          primary={
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 0.5 }}
                              >
                                {event.metadata?.time_formatted || 'Time TBD'}
                                {event.metadata?.location && ` - ${event.metadata.location}`}
                              </Typography>
                              <Typography variant="body1" component="div">
                                {event.content || event.title}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              </Box>
            );
          })
        )}
      </Paper>
    </Box>
  );
};

export default PresidentialSchedule;
